import { Box } from '@mui/material'
import { useEffect, useReducer } from 'react'

import { type Option, type Color } from 'src/types'

import useKeyPress from 'src/hooks/useKeyPress'

import OptionButton from './OptionButton'

type OptionsProps = {
  options: Option[]
  handleOption: (arg0: any) => any
  handleDefaultOption: () => void
  color?: Color
  backgroundColor?: Color
  borderColor?: Color
  logCue: string
}

const style = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px'
}

type OptionSelectState = {
  optionIndex: number
}

type OptionSelectAction = 
{
  type: 'ArrowRight' | 'ArrowLeft' | 'ArrowUp' | 'ArrowDown'
  list: any[]
}

const reducer = (state: OptionSelectState, action: OptionSelectAction) => {
  switch (action.type) {
    case 'ArrowLeft':
    case 'ArrowUp':
      return {
        ...state,
        optionIndex:
          state.optionIndex > 0
            ? state.optionIndex - 1 : action.list.length > 0
              ? action.list.length - 1 : 0
      }
    case 'ArrowRight':
    case 'ArrowDown':
      return {
        ...state,
        optionIndex:
          state.optionIndex + 1 > action.list.length - 1
            ? 0 : state.optionIndex + 1
      }
    default:
      throw new Error('Unexpected action')
  }
}


const Options = ({ options, handleOption, handleDefaultOption, color = 'white', backgroundColor = '#161124', borderColor = 'black', logCue}: OptionsProps) => {
  const isMobile = window.innerWidth <= 768
  const initialOptionSelectState = { optionIndex: isMobile ? -1 : 0 }
  const [state, dispatch] = useReducer(reducer, initialOptionSelectState)
  
  // Ensure first option is always selected when there's only one option (like "leave")
  useEffect(() => {
    if (options.length === 1) {
      // When there's only one option, always select it
      if (state.optionIndex !== 0) {
        // Reset to first option when there's only one
        dispatch({ type: 'ArrowRight', list: options })
      }
    } else if (options.length > 1 && state.optionIndex >= options.length) {
      // If we have multiple options but the selected index is out of bounds, reset to 0
      dispatch({ type: 'ArrowRight', list: options })
    }
  }, [options, state.optionIndex])
  
  const arrowLeftPressed = useKeyPress('ArrowLeft')
  const arrowRightPressed = useKeyPress('ArrowRight')
  const arrowUpPressed = useKeyPress('ArrowUp')
  const arrowDownPressed = useKeyPress('ArrowDown')
  const aPressed = useKeyPress('a')
  const dPressed = useKeyPress('d')
  const wPressed = useKeyPress('w')
  const sPressed = useKeyPress('s')
  const enterPressed = useKeyPress('Enter')

  useEffect(() => {
    if (arrowLeftPressed) {
      dispatch({ type: 'ArrowLeft', list: options })
    }
  }, [arrowLeftPressed, options])

  useEffect(() => {
    if (arrowRightPressed) {
      dispatch({ type: 'ArrowRight', list: options })
    }
  }, [arrowRightPressed, options])

  useEffect(() => {
    if (arrowUpPressed) {
      dispatch({ type: 'ArrowUp', list: options })
    }
  }, [arrowUpPressed, options])

  useEffect(() => {
    if (arrowDownPressed) {
      dispatch({ type: 'ArrowDown', list: options })
    }
  }, [arrowDownPressed, options])

  useEffect(() => {
    if (aPressed) {
      dispatch({ type: 'ArrowLeft', list: options })
    }
  }, [aPressed, options])

  useEffect(() => {
    if (dPressed) {
      dispatch({ type: 'ArrowRight', list: options })
    }
  }, [dPressed, options])

  useEffect(() => {
    if (wPressed) {
      dispatch({ type: 'ArrowUp', list: options })
    }
  }, [wPressed, options])

  useEffect(() => {
    if (sPressed) {
      dispatch({ type: 'ArrowDown', list: options })
    }
  }, [sPressed, options])

  useEffect(() => {
    if (enterPressed) {
      if (options[state.optionIndex]) {
        handleOption(options[state.optionIndex])
      } else {
        handleDefaultOption()
      }
    }
  }, [enterPressed])

  // activate hover state

  return (
    <Box sx={style} className='options'>
      { options && options.length > 0 ? (
        options?.map((option: Option, optionIndex: number) => (
          <OptionButton
            key={optionIndex}
            text={option.text}
            handleClick={() => { handleOption(option) }}
            color={optionIndex === state.optionIndex ? 'gold' : color}
            backgroundColor={backgroundColor}
            borderColor={optionIndex === state.optionIndex ? 'gold' : borderColor}
          />
        ))
      ) : (
        <OptionButton
          text={logCue}
          handleClick={handleDefaultOption}
          color='gold'
          backgroundColor={backgroundColor}
          borderColor='gold'
        />
      )}
    </Box>
  )
}

export default Options
